import React, { useState, useContext, useMemo } from "react";
import styled from "styled-components";
import { device } from "../../lib/breakpoints";
import { marketingSectionHeading } from "../../styles/marketingSectionTypography";
import Button from "../../components/Button";
import { trackEvent } from "../../helpers/mixpanel";
import { useNavigate } from "react-router-dom";
import IntakeModal from "../../components/IntakeModal";
import { AuthContext } from "../../context/AuthProvider";
import {
  isLimitedToOpsChatExperience,
  OPS_CHAT_LANDLORD_HOME,
} from "../../helpers/accountProduct";
import { APP_NAME, TAGLINE_SENTENCE } from "../../helpers/constants";
import {
  ENTERPRISE_TIER,
  formatAnnualDisplayUsd,
  formatMonthlyUsd,
  FREE_TIER,
  getAnnualDisplayUsd,
  getEnterpriseSalesHref,
  getFullOperatingSystemTierKey,
  getPricingSchedule,
  getPricingScheduleWithFree,
  openEnterpriseSalesWindow,
} from "../../lib/pricing";
import FeatureMatrix from "../../components/FeatureMatrix";
import {
  LockOutlined,
  PhoneOutlined,
  ThunderboltOutlined,
  DollarOutlined,
} from "@ant-design/icons";

/** Short labels + pitch above each plan card (market_2026 five-tier ladder). */
const PLAN_COLUMN_COPY = {
  free: {
    headline: "Free Gateway",
    pitch: "1-3 units · Full LandlordOps OS",
  },
  starter: {
    headline: "Starter portfolio",
    pitch: "Full OS · up to 15 doors",
  },
  growth: {
    headline: "Growing portfolio",
    pitch: "Full OS · up to 50 doors",
  },
  scale: {
    headline: "Large portfolio",
    pitch: "Full OS · up to 150 doors",
  },
  enterprise: {
    headline: "Institutional Excellence",
    pitch:
      "For 150+ door operators. Custom workflows, dedicated account manager, SLA guarantees, and white-label options.",
  },
};

/** Primary CTA on Scale tier cards (home + /platform grid and final banner). */
const SCALE_TIER_GRID_CTA = "Get Scale";

/** Text on gold-gradient CTAs: warm brown reads softer than pure black on metallic gold. */
const GOLD_CTA_TEXT = "#3a3024";

/**
 * @param {{ id?: string; variant?: "default" | "fullOs" }} props
 * - `default`: home pricing (Starter / Growth / Scale / Enterprise on 2026 schedule).
 * - `fullOs`: single plan: highest tier that includes your Rental Command Center for the active schedule (/platform).
 */
const PricingSection = ({ id: sectionId = "pricing", variant = "default" }) => {
  const [showIntakeModal, setShowIntakeModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [billingInterval, setBillingInterval] = useState("monthly");
  const { user, userInfo } = useContext(AuthContext);
  const pricingTierGroup = user?.pricingTierGroup || "market_2026";
  /**
   * Homepage self-service grid: Free + Starter + Growth + Scale (4 cards).
   * Enterprise is a full-width sidecar below the grid.
   * /platform fullOs: Scale + Enterprise grid cards (no sidecar; Enterprise is inline).
   */
  const activePricing = useMemo(() => {
    if (variant === "fullOs") {
      return {
        ...getPricingSchedule(pricingTierGroup),
        enterprise: {
          key: "enterprise",
          name: ENTERPRISE_TIER.name,
          type: ENTERPRISE_TIER.type,
          monthlyUSD: ENTERPRISE_TIER.monthlyUSD,
          unitCap: ENTERPRISE_TIER.unitCap,
          stripePriceId: ENTERPRISE_TIER.stripePriceId,
          tagline: ENTERPRISE_TIER.tagline,
          priceLabel: ENTERPRISE_TIER.priceLabel,
          startingAtLabel: ENTERPRISE_TIER.startingAtLabel,
          contactUrl: ENTERPRISE_TIER.contactUrl,
          ctaLabel: ENTERPRISE_TIER.ctaLabel,
          features: [
            `Up to ${ENTERPRISE_TIER.unitCap} units`,
            ...ENTERPRISE_TIER.features,
          ],
        },
      };
    }
    return getPricingScheduleWithFree(pricingTierGroup);
  }, [variant, pricingTierGroup]);
  const navigate = useNavigate();
  const fullOsTierKey = useMemo(
    () => getFullOperatingSystemTierKey(pricingTierGroup),
    [pricingTierGroup]
  );
  const analyticsProduct =
    variant === "fullOs" ? "full_os_page" : "unified_ladder";

  const getTrackedPrice = (planKey, plan) => {
    if (!plan || plan.monthlyUSD === 0) return 0;
    if (billingInterval === "annual") {
      const y = getAnnualDisplayUsd(planKey, pricingTierGroup);
      return y ?? plan.monthlyUSD;
    }
    return plan.monthlyUSD;
  };

  const handlePlanSelect = (planKey, planPrice) => {
    trackEvent("Pricing Plan Selected", {
      plan: planKey,
      price: planPrice,
      billing_cycle: billingInterval,
      product: analyticsProduct,
    });

    setSelectedPlan(planKey);
    setShowIntakeModal(true);
  };

  const handleIntakeSubmit = async (profileData) => {
    try {
      console.log("Profile data submitted:", profileData);
      setShowIntakeModal(false);
      setSelectedPlan(null);
    } catch (error) {
      console.error("Error processing intake:", error);
    }
  };

  const handleCloseModal = () => {
    setShowIntakeModal(false);
    setSelectedPlan(null);
  };

  const handlePlanCTA = (planKey, planPrice) => {
    if (planKey === "enterprise") {
      trackEvent("enterprise_schedule_migration_clicked", {
        source: variant === "fullOs" ? "platform_pricing_grid" : "home_pricing",
        url: getEnterpriseSalesHref(),
      });
      openEnterpriseSalesWindow();
      return;
    }
    if (user) {
      navigate(
        isLimitedToOpsChatExperience(userInfo)
          ? OPS_CHAT_LANDLORD_HOME
          : "/dashboard"
      );
      return;
    }
    if (planKey === "free") {
      trackEvent("Pricing Plan Selected", {
        plan: "free",
        price: 0,
        billing_cycle: "none",
        product: analyticsProduct,
      });
      setSelectedPlan("free");
      setShowIntakeModal(true);
      return;
    }
    handlePlanSelect(planKey, planPrice);
  };

  const handleEnterpriseCTA = () => {
    trackEvent("Pricing Plan Selected", {
      plan: "enterprise",
      price: ENTERPRISE_TIER.monthlyUSD,
      billing_cycle: "quote",
      product: analyticsProduct,
    });
    trackEvent("enterprise_schedule_migration_clicked", {
      source: "home_pricing_sidecar",
      url: getEnterpriseSalesHref(),
    });
    openEnterpriseSalesWindow();
  };

  /** Self-service grid columns: Free · Starter · Growth · Scale (no Enterprise on home). */
  const planOrder =
    variant === "fullOs"
      ? [fullOsTierKey, "enterprise"].filter((k) => activePricing[k])
      : pricingTierGroup === "legacy_2025"
      ? ["free", "starter", "pro", "growth", "scale"]
      : ["free", "starter", "growth", "scale"];
  const planGridCols = planOrder.filter((k) => activePricing[k]).length;

  const columnCopy = (scheduleKey) =>
    PLAN_COLUMN_COPY[scheduleKey] || {
      headline: activePricing[scheduleKey]?.name || scheduleKey,
      pitch: activePricing[scheduleKey]?.tagline || "",
    };

  return (
    <Container id={sectionId}>
      <Content>
        <Header>
          {variant === "fullOs" ? (
            <>
              <Title>Your Rental Command Center</Title>
              <Subtitle>
                <strong>Every plan</strong> includes verified rent collection,
                Automated Triage &amp; Dispatch, vendor coordination, portfolio
                analytics, Schedule E exports, and Leo AI - pick the door count
                that fits your portfolio.
              </Subtitle>
              <TrustBadges>
                <TrustBadge>Full LandlordOps OS on every tier</TrustBadge>
                <TrustBadge>Up to the listed door limit</TrustBadge>
                <TrustBadge>
                  {billingInterval === "annual"
                    ? "Annual billing · save ~17%"
                    : "Monthly billing · cancel anytime"}
                </TrustBadge>
              </TrustBadges>
            </>
          ) : (
            <>
              <Title>Pick the plan that matches your door count.</Title>
              <Subtitle>
                Every tier includes the full LandlordOps OS - rent collection,
                Schedule E, vendors, ledgers, and Leo. You only pay for doors.
              </Subtitle>
              <TrustBadges>
                <TrustBadge>Free: 1-3 units</TrustBadge>
                <TrustBadge>Same OS on every plan</TrustBadge>
                <TrustBadge>Cancel anytime</TrustBadge>
              </TrustBadges>
            </>
          )}
        </Header>

        <BillingIntervalRow>
          <IntervalToggle
            role="group"
            aria-label="Choose monthly or annual billing"
          >
            <IntervalButton
              type="button"
              $active={billingInterval === "monthly"}
              onClick={() => setBillingInterval("monthly")}
            >
              Monthly
            </IntervalButton>
            <IntervalButton
              type="button"
              $active={billingInterval === "annual"}
              onClick={() => setBillingInterval("annual")}
            >
              <span className="interval-label">Annual</span>
              <AnnualIncentiveBadge>2 months free</AnnualIncentiveBadge>
            </IntervalButton>
          </IntervalToggle>
        </BillingIntervalRow>

        <PlansGrid
          $cols={planGridCols}
          $single={variant === "fullOs" && planGridCols <= 1}
          $fullOsPair={variant === "fullOs" && planGridCols >= 2}
        >
          {planOrder.map((scheduleKey) => {
            const plan = activePricing[scheduleKey];
            if (!plan) return null;
            const copy = columnCopy(scheduleKey);
            const planKeyForIntake = scheduleKey;
            const isFreeCard = scheduleKey === "free";
            const isScaleCard = scheduleKey === "scale";
            const isEnterpriseCard = scheduleKey === "enterprise";
            const isStarterCard =
              variant !== "fullOs" && scheduleKey === "starter";
            const isScaleTierGoldCta =
              !isEnterpriseCard &&
              (scheduleKey === "scale" ||
                (variant === "fullOs" && scheduleKey === fullOsTierKey));
            const annualUsd = getAnnualDisplayUsd(
              scheduleKey,
              pricingTierGroup
            );
            const showAnnualPrice =
              billingInterval === "annual" &&
              annualUsd != null &&
              !isFreeCard &&
              plan.monthlyUSD > 0 &&
              !(isEnterpriseCard && plan.type === "contact");
            return (
              <PlanCard
                key={scheduleKey}
                $freeGateway={variant !== "fullOs" && isFreeCard}
                $featured={variant !== "fullOs" && isScaleCard}
                $popular={
                  variant !== "fullOs" && !isScaleCard && !!plan.popular
                }
                $growthGlow={false}
                $starterValue={isStarterCard}
              >
                {isFreeCard && variant !== "fullOs" ? (
                  <FreeGatewayBadge>$0 · No card</FreeGatewayBadge>
                ) : isEnterpriseCard && variant === "fullOs" ? (
                  <EnterpriseGridBadge>Institutional</EnterpriseGridBadge>
                ) : isScaleCard && variant !== "fullOs" ? (
                  <FeaturedOsBadge>Most doors</FeaturedOsBadge>
                ) : isStarterCard ? (
                  <StarterValueTopBadge>
                    Full OS · 15 doors
                  </StarterValueTopBadge>
                ) : (
                  plan.popular &&
                  !isScaleCard && <PopularBadge>Most Popular</PopularBadge>
                )}

                <PlanContent>
                  <PlanHeader>
                    <ColumnHeadline>{copy.headline}</ColumnHeadline>
                    <PlanName $featured={isScaleCard && variant !== "fullOs"}>
                      {plan.name}
                    </PlanName>
                    <PlanTagline>{copy.pitch}</PlanTagline>
                    {copy.socialProof && (
                      <SocialProofLine>{copy.socialProof}</SocialProofLine>
                    )}

                    <PriceContainer>
                      {isEnterpriseCard && plan.type === "contact" ? (
                        <EnterprisePricingSummary>
                          {ENTERPRISE_TIER.startingAtLabel}
                        </EnterprisePricingSummary>
                      ) : (
                        <>
                          <Price>
                            {plan.monthlyUSD === 0 ? (
                              <PriceAmount>FREE</PriceAmount>
                            ) : showAnnualPrice ? (
                              <>
                                <PriceAmount>
                                  ${formatAnnualDisplayUsd(annualUsd)}
                                </PriceAmount>
                                <PricePeriod>/year</PricePeriod>
                              </>
                            ) : (
                              <>
                                <PriceAmount>
                                  ${formatMonthlyUsd(plan.monthlyUSD)}
                                </PriceAmount>
                                <PricePeriod>/month</PricePeriod>
                              </>
                            )}
                          </Price>
                          {showAnnualPrice && (
                            <BilledAnnuallyText>
                              Billed annually
                            </BilledAnnuallyText>
                          )}
                          <UnitRange>{plan.features[0]}</UnitRange>
                        </>
                      )}
                    </PriceContainer>
                  </PlanHeader>

                  {/* Door cap is features[0]; show the five full-OS core bullets */}
                  <FeatureList>
                    {plan.features.slice(1, 6).map((f) => (
                      <FeatureLi key={f}>{f}</FeatureLi>
                    ))}
                  </FeatureList>
                </PlanContent>

                <PlanCTA>
                  <CTAButton
                    $popular={isScaleCard || plan.popular}
                    $free={isFreeCard}
                    $scaleGold={isScaleTierGoldCta}
                    onClick={() =>
                      handlePlanCTA(
                        planKeyForIntake,
                        getTrackedPrice(planKeyForIntake, plan)
                      )
                    }
                  >
                    {planKeyForIntake === "enterprise" ? (
                      ENTERPRISE_TIER.ctaLabel
                    ) : user ? (
                      "Go to Dashboard"
                    ) : isFreeCard ? (
                      <FreeCtaLines>
                        <FreeCtaPrimary>{FREE_TIER.ctaLabel}</FreeCtaPrimary>
                        <FreeCtaSecondary>
                          {FREE_TIER.ctaSubline}
                        </FreeCtaSecondary>
                      </FreeCtaLines>
                    ) : isScaleCard ||
                      (variant === "fullOs" &&
                        scheduleKey === fullOsTierKey) ? (
                      SCALE_TIER_GRID_CTA
                    ) : (
                      "Get Started"
                    )}
                  </CTAButton>
                  <CTASubtext>
                    {isFreeCard ? (
                      <>
                        <CTALine>No credit card</CTALine>
                        <CTALine>Up to {FREE_TIER.unitCap} doors</CTALine>
                      </>
                    ) : isEnterpriseCard ? (
                      <>
                        <CTALine>Custom SLA</CTALine>
                        <CTALine>Migration &amp; white-label</CTALine>
                      </>
                    ) : (
                      <>
                        <CTALine>
                          {billingInterval === "annual"
                            ? "Annual subscription"
                            : "Monthly subscription"}
                        </CTALine>
                        <CTALine>Cancel anytime</CTALine>
                      </>
                    )}
                  </CTASubtext>
                </PlanCTA>
              </PlanCard>
            );
          })}
        </PlansGrid>

        {/* Enterprise sidecar: quote-based tier; not in self-serve intake */}
        {variant !== "fullOs" && (
          <EnterpriseSidecar>
            <EnterpriseSidecarInner>
              <EnterpriseSidecarLeft>
                <EnterpriseSidecarEyebrow>
                  150+ Door Operators
                </EnterpriseSidecarEyebrow>
                <EnterpriseSidecarTitle>
                  Institutional Excellence
                </EnterpriseSidecarTitle>
                <EnterpriseSidecarTagline>
                  {ENTERPRISE_TIER.startingAtLabel}
                </EnterpriseSidecarTagline>
                <EnterpriseSidecarFeatures>
                  {ENTERPRISE_TIER.features.map((f) => (
                    <EnterpriseSidecarFeatureItem key={f}>
                      {f}
                    </EnterpriseSidecarFeatureItem>
                  ))}
                </EnterpriseSidecarFeatures>
              </EnterpriseSidecarLeft>
              <EnterpriseSidecarRight>
                <EnterpriseSidecarCTA
                  onClick={handleEnterpriseCTA}
                  type="button"
                >
                  {ENTERPRISE_TIER.ctaLabel}
                </EnterpriseSidecarCTA>
                <EnterpriseSidecarSubtext>
                  Custom SLA · up to {ENTERPRISE_TIER.unitCap} units ·
                  white-label
                </EnterpriseSidecarSubtext>
              </EnterpriseSidecarRight>
            </EnterpriseSidecarInner>
          </EnterpriseSidecar>
        )}

        {variant !== "fullOs" && <FeatureMatrix />}

        {/* FAQ removed here to avoid duplication with the main accordion FAQ on Home */}

        <TrustSection>
          {/* <TrustTitle>Trusted by landlords nationwide</TrustTitle> */}
          <TrustFeatures>
            <TrustFeature>
              <TrustIcon>
                <LockOutlined />
              </TrustIcon>
              <TrustText>Bank-level security</TrustText>
            </TrustFeature>
            <TrustFeature>
              <TrustIcon>
                <PhoneOutlined />
              </TrustIcon>
              <TrustText>Daily support availability</TrustText>
            </TrustFeature>
            <TrustFeature>
              <TrustIcon>
                <ThunderboltOutlined />
              </TrustIcon>
              <TrustText>99.9% uptime</TrustText>
            </TrustFeature>
            <TrustFeature>
              <TrustIcon>
                <DollarOutlined />
              </TrustIcon>
              <TrustText>No hidden fees</TrustText>
            </TrustFeature>
          </TrustFeatures>
        </TrustSection>

        <FinalCTASection>
          <FinalCTATitle>
            {variant === "fullOs"
              ? "Everything you need on one plan"
              : "Ready for quieter evenings?"}
          </FinalCTATitle>
          <FinalCTASubtitle>
            {variant === "fullOs"
              ? `Subscribe at the tier above for ${APP_NAME} rent, vendors, analytics, and Schedule E ready exports, plus Leo AI in tenant chat and on your dashboard.`
              : `Start free with the full OS on one to three units. Upgrade only when you need more doors.`}
          </FinalCTASubtitle>
          <FinalCTAButton
            onClick={() => {
              const key = variant === "fullOs" ? fullOsTierKey : "free";
              const entry = activePricing[key];
              if (entry) handlePlanCTA(key, getTrackedPrice(key, entry));
            }}
          >
            {user
              ? "Go to Dashboard"
              : variant === "fullOs"
              ? SCALE_TIER_GRID_CTA
              : "Get Started for Free"}
          </FinalCTAButton>
          <FinalCTASubtext>
            {billingInterval === "annual"
              ? "Annual billing when you upgrade"
              : "Monthly billing when you upgrade"}
            . Cancel anytime.
          </FinalCTASubtext>
        </FinalCTASection>
      </Content>

      {showIntakeModal && selectedPlan && (
        <IntakeModal
          isOpen={showIntakeModal}
          onClose={handleCloseModal}
          selectedPlan={selectedPlan}
          checkoutBillingCycle={
            billingInterval === "annual" ? "annual" : "monthly"
          }
          onSubmit={handleIntakeSubmit}
        />
      )}
    </Container>
  );
};

export default PricingSection;

// Styled Components
const Container = styled.section`
  background: linear-gradient(135deg, #f8faf8 0%, #f0f7f0 100%);
  position: relative;
  padding: 6rem 2rem;
  overflow: hidden;

  @media ${device.mobile} {
    padding: 4rem 1rem;
  }
`;

const Content = styled.div`
  max-width: 1320px;
  margin: 0 auto;
  position: relative;
  z-index: 2;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 1.5rem;
`;

const BillingIntervalRow = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 2rem;
`;

const IntervalToggle = styled.div`
  display: inline-flex;
  padding: 4px;
  border-radius: 9999px;
  background: #eef2ed;
  box-shadow: 0 2px 10px rgba(15, 23, 42, 0.06);
  border: 1px solid rgba(44, 95, 45, 0.12);
`;

const IntervalButton = styled.button`
  border: none;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  padding: 0.5rem 1.15rem;
  border-radius: 9999px;
  font-size: 0.9rem;
  font-weight: 600;
  font-family: var(--font-secondary, "Inter", sans-serif);
  color: #4b5563;
  background: transparent;
  transition: background 0.2s ease, color 0.2s ease, box-shadow 0.2s ease;

  .interval-label {
    font-size: inherit;
    font-weight: inherit;
    line-height: inherit;
  }

  ${(p) =>
    p.$active
      ? `
    background: #2c5f2d;
    color: #fff;
    box-shadow: 0 2px 8px rgba(44, 95, 45, 0.3);
  `
      : `
    &:hover {
      color: #2c5f2d;
    }
  `}
`;

const AnnualIncentiveBadge = styled.span`
  display: inline-block;
  font-size: 0.64rem;
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  padding: 0.2rem 0.45rem;
  border-radius: 6px;
  background: #cdaf63;
  color: #1f1b14;
  line-height: 1.2;
`;

const BilledAnnuallyText = styled.p`
  margin: 0.15rem 0 0;
  font-size: 0.8rem;
  color: var(--color-text-muted);
  font-family: var(--font-secondary, "Inter", sans-serif);
  text-align: center;
`;

const Title = styled.h2`
  ${marketingSectionHeading}
  color: var(--color-text);
  margin-bottom: 1.5rem;
`;

const Subtitle = styled.p`
  font-size: 1.15rem;
  color: var(--color-text-light);
  font-family: var(--font-secondary);
  margin-bottom: 1.25rem;
  line-height: 1.5;
  max-width: 36rem;
  margin-left: auto;
  margin-right: auto;
`;

const ColumnHeadline = styled.div`
  font-size: 0.78rem;
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--color-primary-darker);
  font-family: var(--font-secondary);
  margin-bottom: 0.5rem;
`;

const TrustBadges = styled.div`
  display: flex;
  justify-content: center;
  gap: 1.5rem;
  flex-wrap: wrap;
  margin-top: 0.5rem;

  @media ${device.mobile} {
    gap: 1rem;
  }
`;

const TrustBadge = styled.span`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9rem;
  padding: 0.5rem 1rem;
  background: var(--color-success);
  color: white;
  border-radius: 20px;
  font-weight: 600;
  font-family: var(--font-secondary);

  &::before {
    content: "✓";
    font-weight: bold;
  }
`;

const SocialProofLine = styled.p`
  font-size: 0.8125rem;
  color: var(--color-text-muted);
  font-family: var(--font-secondary);
  margin: 0;
  line-height: 1.4;
  font-style: italic;
`;

const PlansGrid = styled.div`
  display: grid;
  gap: 1.5rem;
  margin-bottom: 3rem;
  align-items: stretch;

  grid-template-columns: ${({ $single, $cols }) => {
    const c = typeof $cols === "number" ? $cols : 4;
    if ($single) return "1fr";
    return `repeat(${c}, minmax(0, 1fr))`;
  }};

  & > * {
    min-height: 0;
    height: 100%;
    align-self: stretch;
  }
  ${(p) =>
    p.$single
      ? `
    max-width: 440px;
    margin-left: auto;
    margin-right: auto;
  `
      : ""}
  ${(p) =>
    p.$fullOsPair
      ? `
    max-width: 960px;
    margin-left: auto;
    margin-right: auto;
  `
      : ""}

  @media (max-width: 1080px) {
    grid-template-columns: ${({ $single, $cols }) => {
      const c = typeof $cols === "number" ? $cols : 4;
      if ($single) return "1fr";
      const tabletCols = c >= 4 ? 2 : c;
      return `repeat(${tabletCols}, minmax(0, 1fr))`;
    }};
  }

  @media ${device.mobile} {
    grid-template-columns: 1fr;
    /* Room for Starter’s overlapping badge (top: negative) vs the card above */
    gap: 2.5rem;
  }
`;

const FeatureList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  text-align: left;
`;

const FeatureLi = styled.li`
  font-size: 0.9rem;
  color: var(--color-text-light);
  font-family: var(--font-secondary);
  line-height: 1.45;
  padding: 0.35rem 0;
  padding-left: 1.1rem;
  position: relative;
  &::before {
    content: "✓";
    position: absolute;
    left: 0;
    color: var(--color-primary);
    font-weight: 700;
  }
`;

const PlanCard = styled.div`
  background: #fff;
  border-radius: 20px;
  padding: 2.5rem 1.75rem;
  position: relative;
  border: 3px solid
    ${(props) =>
      props.$freeGateway
        ? "#1d8f52"
        : props.$featured
        ? "#0f3d12"
        : props.$popular
        ? "var(--color-primary)"
        : props.$starterValue
        ? "rgba(44, 95, 45, 0.45)"
        : "var(--color-border-light)"};
  box-shadow: ${(props) =>
    props.$freeGateway
      ? "0 0 0 1px rgba(29, 143, 82, 0.45), 0 12px 36px rgba(29, 143, 82, 0.2)"
      : props.$featured
      ? "0 0 0 2px rgba(15, 61, 18, 0.35), 0 18px 52px rgba(44, 95, 45, 0.28)"
      : props.$popular
      ? "0 8px 32px rgba(44, 95, 45, 0.15)"
      : props.$starterValue
      ? "0 0 0 2px rgba(44, 95, 45, 0.12), 0 8px 28px rgba(44, 95, 45, 0.1)"
      : "0 4px 20px var(--shadow-light)"};
  /* No default translateY on featured - transform shifts painted content vs siblings and misaligns CTAs */
  transform: none;
  z-index: ${(props) => (props.$featured ? 3 : 2)};
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  height: 100%;
  min-width: 0;
  max-width: 100%;
  box-sizing: border-box;

  @media ${device.mobile} {
    padding: 2rem 1.15rem;
    transform: none;
  }

  &:hover {
    /* Same lift for every card so primary buttons stay aligned across columns */
    transform: translateY(-4px);
    box-shadow: ${(props) =>
      props.$freeGateway
        ? "0 0 0 2px rgba(29, 143, 82, 0.55), 0 16px 44px rgba(29, 143, 82, 0.22)"
        : props.$featured
        ? "0 0 0 2px rgba(15, 61, 18, 0.45), 0 22px 60px rgba(44, 95, 45, 0.32)"
        : props.$popular
        ? "0 12px 40px rgba(44, 95, 45, 0.2)"
        : props.$starterValue
        ? "0 0 0 2px rgba(44, 95, 45, 0.16), 0 12px 36px rgba(44, 95, 45, 0.14)"
        : "0 8px 32px var(--shadow-medium)"};
  }
`;

const PopularBadge = styled.div`
  position: absolute;
  top: -12px;
  left: 50%;
  transform: translateX(-50%);
  background: var(--gradient-primary);
  color: #fff;
  padding: 0.5rem 1.25rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  font-family: var(--font-secondary);
  box-shadow: var(--shadow-medium);
  white-space: nowrap;
`;

/** Scale tier: distinct from “Most Popular” (Growth). */
const FeaturedOsBadge = styled(PopularBadge)`
  letter-spacing: 0.04em;
`;

/** Enterprise on /platform grid (alongside Scale). */
const EnterpriseGridBadge = styled(PopularBadge)`
  background: linear-gradient(135deg, #1f2937 0%, #111827 100%);
  letter-spacing: 0.05em;
  box-shadow: 0 4px 18px rgba(0, 0, 0, 0.25);
`;

const HighestValueBadge = styled.div`
  position: absolute;
  top: -12px;
  left: 50%;
  transform: translateX(-50%);
  background: linear-gradient(135deg, #eab308 0%, #a16207 100%);
  color: #fff;
  padding: 0.5rem 1.25rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  font-family: var(--font-secondary);
  box-shadow: 0 4px 16px rgba(202, 138, 4, 0.35);
  white-space: nowrap;
`;

const FreeGatewayBadge = styled.div`
  position: absolute;
  top: -12px;
  left: 50%;
  transform: translateX(-50%);
  background: linear-gradient(135deg, #2cb76b 0%, #1d8f52 100%);
  color: #fff;
  padding: 0.5rem 1.1rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 700;
  font-family: var(--font-secondary);
  box-shadow: 0 4px 14px rgba(44, 183, 107, 0.35);
  white-space: nowrap;
`;

/* EnterpriseBadge removed: Enterprise is now the full-width sidecar below the grid */

const StarterValueTopBadge = styled.div`
  position: absolute;
  /* Same vertical anchor as PopularBadge / FreeGatewayBadge / FeaturedOsBadge */
  top: -12px;
  left: 50%;
  transform: translateX(-50%);
  max-width: calc(100% - 1.5rem);
  background: #fff;
  color: var(--color-primary-darker);
  border: 2px solid var(--color-primary);
  padding: 0.5rem 0.85rem;
  border-radius: 20px;
  font-size: 0.65rem;
  font-weight: 600;
  font-family: var(--font-secondary);
  box-shadow: 0 4px 14px rgba(44, 95, 45, 0.12);
  white-space: nowrap;
  text-align: center;
  line-height: 1.25;

  @media ${device.mobile} {
    font-size: clamp(0.58rem, 2.6vw, 0.65rem);
  }
`;

const HighlightBadge = styled.div`
  position: absolute;
  top: -12px;
  left: 50%;
  transform: translateX(-50%);
  background: var(--color-accent);
  color: #fff;
  padding: 0.5rem 1.5rem;
  border-radius: 20px;
  font-size: 0.9rem;
  font-weight: 600;
  font-family: var(--font-secondary);
  box-shadow: var(--shadow-medium);
`;

const PlanContent = styled.div`
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  min-height: 0;
`;

const PlanHeader = styled.div`
  text-align: center;
  margin-bottom: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
`;

const PlanName = styled.h3`
  font-size: ${(p) =>
    p.$featured
      ? "clamp(1.6rem, 4.5vw, 2.25rem)"
      : "clamp(1.35rem, 4.5vw, 2rem)"};
  font-weight: 700;
  color: var(--color-primary);
  margin: 0;
  font-family: var(--font-primary);
  letter-spacing: -0.02em;
  word-break: break-word;
  /* Keeps dollar amounts aligned across columns when tier names use different sizes */
  min-height: 3rem;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const PlanTagline = styled.p`
  font-size: 0.9375rem;
  color: var(--color-text-light);
  margin: 0;
  font-family: var(--font-secondary);
  font-style: normal;
  line-height: 1.5;
  max-width: 22rem;
  margin-left: auto;
  margin-right: auto;
  /* Reserve four lines so short pitches don’t pull price above Scale’s block */
  min-height: calc(0.9375rem * 1.5 * 4);
  box-sizing: border-box;
`;

const PriceContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.5rem;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
`;

const Price = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: center;
  flex-wrap: wrap;
  gap: 0.15rem 0.35rem;
  max-width: 100%;
  min-width: 0;
`;

const PriceAmount = styled.span`
  font-size: clamp(1.45rem, min(10vw, 11vmin), 3.5rem);
  font-weight: 700;
  font-family: "Poppins", var(--font-primary, sans-serif);
  color: var(--color-primary);
  letter-spacing: -0.03em;
  line-height: 1.05;
  min-width: 0;
  max-width: 100%;
`;

const PricePeriod = styled.span`
  font-size: clamp(0.78rem, min(2.8vw, 3.5vmin), 1.1rem);
  color: var(--color-text-light);
  font-family: var(--font-secondary);
  flex-shrink: 0;
`;

const UnitRange = styled.div`
  font-size: 0.9rem;
  color: var(--color-text-muted);
  font-family: var(--font-secondary);
  font-weight: 500;
`;

/** Enterprise: no dollar headline; single line under the pitch. */
const EnterprisePricingSummary = styled.p`
  margin: 0.5rem 0 0;
  padding: 0 0.25rem;
  font-size: 0.95rem;
  line-height: 1.45;
  color: var(--color-text-muted);
  font-family: var(--font-secondary);
  font-weight: 600;
  text-align: center;
  max-width: 19rem;
`;

const FeatureSheetRef = styled.p`
  font-size: 0.9375rem;
  color: var(--color-text-light);
  font-family: var(--font-secondary);
  line-height: 1.5;
  margin: 0;
  text-align: center;
  padding: 0.5rem 0;
`;

const FeaturesList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const FeatureItem = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
  font-family: var(--font-secondary);
  color: var(--color-text);
  line-height: 1.5;
  text-align: left;

  @media ${device.mobile} {
    justify-content: center;
    text-align: center;
  }
`;

const CheckIcon = styled.span`
  background: var(--color-success);
  color: white;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  font-weight: bold;
  flex-shrink: 0;
  margin-top: 2px;
`;

const PlusIcon = styled.span`
  color: var(--color-primary);
  font-weight: bold;
  font-size: 1rem;
`;

const PlanCTA = styled.div`
  margin-top: auto;
  padding-top: 1.35rem;
  text-align: center;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const FreeCtaLines = styled.span`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.2rem;
  width: 100%;
`;

const FreeCtaPrimary = styled.span`
  display: block;
  line-height: 1.2;
`;

const FreeCtaSecondary = styled.span`
  display: block;
  font-size: 0.82em;
  font-weight: 600;
  line-height: 1.2;
  opacity: 0.98;
`;

const CTAButton = styled.button`
  width: 100%;
  padding: 1.15rem clamp(0.85rem, 4vw, 1.35rem);
  font-size: 1.1rem;
  font-weight: 700;
  line-height: 1.25;
  border-radius: 12px;
  font-family: var(--font-secondary);
  letter-spacing: -0.01em;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  margin-bottom: 0.5rem;
  z-index: 2;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  box-sizing: border-box;
  min-height: 52px;

  background: ${(p) =>
    p.$free
      ? "#fff"
      : p.$scaleGold
      ? "linear-gradient(145deg, #f4ecd8 0%, #d4b24a 52%, #a8861c 100%)"
      : "var(--gradient-primary)"};
  color: ${(p) =>
    p.$free ? "var(--color-primary)" : p.$scaleGold ? GOLD_CTA_TEXT : "#fff"};
  border: ${(p) =>
    p.$free
      ? "2px solid var(--color-primary)"
      : p.$scaleGold
      ? "1px solid rgba(92, 72, 18, 0.28)"
      : "none"};
  box-shadow: ${(p) =>
    p.$free
      ? "0 2px 14px rgba(44, 95, 45, 0.1)"
      : p.$scaleGold
      ? "0 4px 16px rgba(168, 134, 40, 0.22)"
      : "0 4px 18px rgba(44, 95, 45, 0.15)"};

  &:hover {
    transform: translateY(-2px);
    ${(p) =>
      p.$free
        ? `
      background: #f4fbf6 !important;
      color: var(--color-primary-darker) !important;
      border-color: var(--color-primary-darker) !important;
      box-shadow: 0 6px 22px rgba(44, 95, 45, 0.16);
    `
        : p.$scaleGold
        ? `
      filter: brightness(1.05);
      box-shadow: 0 8px 24px rgba(148, 118, 36, 0.32);
    `
        : `
      background: var(--gradient-primary-dark) !important;
      color: #fff !important;
      box-shadow: 0 8px 32px rgba(44, 95, 45, 0.25);
    `}
  }

  &:active {
    transform: translateY(0);
  }
`;

const CTASubtext = styled.p`
  font-size: 0.7rem !important;
  color: var(--color-text-light);
  font-family: var(--font-secondary);
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  align-items: center;

  @media (min-width: 769px) {
    font-size: 0.45rem !important;
  }
`;

const CTALine = styled.span`
  display: block;
`;

/* ─── Enterprise Sidecar ────────────────────────────────────────────────────
   Full-width premium band; gold border + glow to read “institutional” vs grid cards.
   ─────────────────────────────────────────────────────────────────────────── */
const EnterpriseSidecar = styled.div`
  --enterprise-gold: #c9a227;
  --enterprise-gold-soft: rgba(201, 162, 39, 0.45);
  --enterprise-gold-glow: rgba(212, 175, 85, 0.55);

  border-radius: 20px;
  padding: 2.75rem 2.5rem;
  margin-bottom: 4rem;
  background: linear-gradient(
    145deg,
    #fffcf5 0%,
    rgba(255, 252, 245, 0.97) 40%,
    rgba(248, 244, 232, 0.95) 100%
  );
  border: 2px solid var(--enterprise-gold);
  box-shadow: 0 0 0 1px rgba(255, 220, 150, 0.5),
    0 0 28px var(--enterprise-gold-soft), 0 0 48px var(--enterprise-gold-glow),
    0 12px 40px rgba(0, 0, 0, 0.06);

  @media ${device.mobile} {
    padding: 2rem 1.25rem;
    border-radius: 16px;
  }
`;

const EnterpriseSidecarInner = styled.div`
  display: flex;
  /* Stretch columns to the same height on desktop so the CTA can sit mid-column */
  align-items: stretch;
  gap: 3rem;

  @media (max-width: 860px) {
    flex-direction: column;
    align-items: center;
    gap: 2rem;
    text-align: center;
  }
`;

const EnterpriseSidecarLeft = styled.div`
  flex: 1;
  min-width: 0;

  @media (min-width: 861px) {
    text-align: left;
  }
`;

const EnterpriseSidecarEyebrow = styled.p`
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--color-primary-darker);
  font-family: var(--font-secondary);
  margin: 0 0 0.5rem;
`;

const EnterpriseSidecarTitle = styled.h3`
  font-size: clamp(1.5rem, 3vw, 2.1rem);
  font-weight: 700;
  font-family: var(--font-primary);
  color: var(--color-text);
  margin: 0 0 0.4rem;
  letter-spacing: -0.02em;
`;

const EnterpriseSidecarTagline = styled.p`
  font-size: 0.95rem;
  color: var(--color-text-light);
  font-family: var(--font-secondary);
  margin: 0 0 1.4rem;
`;

const EnterpriseSidecarFeatures = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.45rem;

  @media (max-width: 860px) and (min-width: 768px) {
    align-items: center;
  }

  @media ${device.mobile} {
    display: block;
    list-style: disc outside;
    padding-left: 1.35rem;
    width: 100%;
    box-sizing: border-box;
    text-align: left;
  }
`;

const EnterpriseSidecarFeatureItem = styled.li`
  font-size: 0.9rem;
  color: var(--color-text);
  font-family: var(--font-secondary);
  padding-left: 1.2rem;
  position: relative;
  text-align: left;
  line-height: 1.45;

  &::before {
    content: "✓";
    position: absolute;
    left: 0;
    color: var(--color-primary);
    font-weight: 700;
  }

  @media (max-width: 860px) and (min-width: 768px) {
    text-align: center;
    padding-left: 0;
    &::before {
      display: none;
    }
  }

  @media ${device.mobile} {
    display: list-item;
    text-align: left;
    padding-left: 0;
    margin-bottom: 0.45rem;
    &::before {
      display: none;
    }
    &:last-child {
      margin-bottom: 0;
    }
  }
`;

const EnterpriseSidecarRight = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  flex-shrink: 0;
  min-width: 200px;

  @media (min-width: 861px) {
    justify-content: center;
  }
`;

const EnterpriseSidecarCTA = styled.button`
  background: linear-gradient(
    145deg,
    #e6c85c 0%,
    var(--enterprise-gold, #c9a227) 52%,
    #b8921f 100%
  );
  color: ${GOLD_CTA_TEXT};
  border: 1px solid rgba(0, 0, 0, 0.12);
  padding: 1rem 2rem;
  border-radius: 12px;
  font-size: 1.05rem;
  font-weight: 700;
  font-family: var(--font-secondary);
  cursor: pointer;
  transition: all 0.25s ease;
  width: 100%;
  box-shadow: 0 4px 16px rgba(201, 162, 39, 0.35);
  white-space: nowrap;
  letter-spacing: -0.01em;

  &:hover {
    filter: brightness(1.06);
    transform: translateY(-2px);
    box-shadow: 0 8px 26px rgba(201, 162, 39, 0.42);
  }
  &:active {
    transform: translateY(0);
  }
`;

const EnterpriseSidecarSubtext = styled.p`
  font-size: 0.75rem;
  color: var(--color-text-muted);
  font-family: var(--font-secondary);
  margin: 0;
  text-align: center;
  line-height: 1.5;
`;

const FAQSection = styled.div`
  margin-bottom: 5rem;
`;

const FAQTitle = styled.h3`
  font-size: 2.5rem;
  font-weight: 700;
  color: var(--color-text);
  text-align: center;
  margin-bottom: 3rem;
  font-family: var(--font-primary);
`;

const FAQGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  gap: 2rem;

  @media ${device.mobile} {
    grid-template-columns: 1fr;
  }
`;

const FAQItem = styled.div`
  background: white;
  padding: 2rem;
  border-radius: 16px;
  border: 1px solid var(--color-border-light);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 32px var(--shadow-light);
  }
`;

const FAQQuestion = styled.h4`
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--color-text);
  margin-bottom: 1rem;
  font-family: var(--font-secondary);
`;

const FAQAnswer = styled.p`
  font-size: 0.95rem;
  color: var(--color-text-light);
  line-height: 1.6;
  font-family: var(--font-secondary);
`;

const TrustSection = styled.div`
  text-align: center;
  margin-top: 4rem;
`;

const TrustTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--color-text);
  margin-bottom: 2rem;
  font-family: var(--font-secondary);
`;

const TrustFeatures = styled.div`
  display: flex;
  justify-content: center;
  gap: 3rem;
  flex-wrap: wrap;

  @media ${device.mobile} {
    gap: 2rem;
  }
`;

const TrustFeature = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
`;

const TrustIcon = styled.div`
  font-size: 2rem;
  margin-bottom: 0.5rem;
  color: var(--color-primary);
  display: flex;
  align-items: center;
  justify-content: center;

  .anticon {
    font-size: 2rem;
    color: var(--color-primary);
  }
`;

const TrustText = styled.span`
  font-size: 0.9rem;
  color: var(--color-text-light);
  font-family: var(--font-secondary);
  font-weight: 500;
`;

const FinalCTASection = styled.div`
  text-align: center;
  margin-top: 5rem;
  padding: 4rem 2rem;
  background: linear-gradient(
    135deg,
    var(--color-primary-dark) 0%,
    var(--color-primary) 100%
  );
  border-radius: 24px;
  color: white;
  box-shadow: 0 8px 32px rgba(44, 95, 45, 0.2);
`;

const FinalCTATitle = styled.h3`
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 1rem;
  font-family: var(--font-primary);
  letter-spacing: -0.02em;
  color: white;

  @media ${device.mobile} {
    font-size: 2rem;
  }
`;

const FinalCTASubtitle = styled.p`
  font-size: 1.2rem;
  margin-bottom: 2rem;
  font-family: var(--font-secondary);
  opacity: 0.9;
  line-height: 1.6;
  color: white;

  @media ${device.minTablet} {
    padding-left: 1.5rem;
    padding-right: 1.5rem;
  }

  @media ${device.desktop} {
    padding-left: 3.5rem;
    padding-right: 3.5rem;
  }
`;

const FinalCTAButton = styled.button`
  background: white;
  color: var(--color-primary);
  border: none;
  padding: 1.25rem 2.5rem;
  border-radius: 12px;
  font-size: 1.1rem;
  font-weight: 700;
  font-family: var(--font-secondary);
  cursor: pointer;
  transition: all 0.3s ease;
  margin-bottom: 1rem;
  min-height: 52px;
  box-shadow: 0 4px 16px rgba(255, 255, 255, 0.2);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(255, 255, 255, 0.3);
  }
`;

const FinalCTASubtext = styled.p`
  font-size: 0.9rem;
  font-family: var(--font-secondary);
  opacity: 0.8;
  margin: 0;
  color: white;
`;
